"use client";

import { useState, useEffect, useRef } from "react";

interface Particle {
  x: number;
  y: number;
  tx: number;
  ty: number;
  size: number;
  alpha: number;
  color: string;
}

export default function Preloader({ onComplete }: { onComplete: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [progress, setProgress] = useState(0);
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let disposed = false;
    let finished = false;
    let finishScheduled = false;
    let raf = 0;
    let started = false;
    let particles: Particle[] = [];

    const finish = () => {
      if (finished || disposed) return;
      finished = true;
      setReveal(true);
      window.dispatchEvent(new CustomEvent("stygian:loaded"));
      window.setTimeout(() => {
        if (!disposed) onComplete();
      }, 650);
    };

    const hardTimer = window.setTimeout(finish, 3000);

    // Cosmetic progress
    const progressId = window.setInterval(() => {
      setProgress((v) => Math.min(100, v + Math.random() * 1.6 + 0.5));
    }, 60);

    // Simple path for reduced motion
    if (reduce) {
      const t = window.setTimeout(finish, 1500);
      return () => {
        disposed = true;
        clearTimeout(t);
        clearTimeout(hardTimer);
        clearInterval(progressId);
      };
    }

    const ctx = canvas ? canvas.getContext("2d") : null;
    if (!canvas || !ctx) {
      finish();
      return () => {
        disposed = true;
        clearTimeout(hardTimer);
        clearInterval(progressId);
      };
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = window.innerWidth;
    let h = window.innerHeight;
    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const onResize = () => resize();
    window.addEventListener("resize", onResize);

    const CX = w / 2;
    const CY = h * 0.4;
    const scale = Math.min(300, w * 0.7) / 96;

    const loop = () => {
      if (disposed) return;
      ctx.clearRect(0, 0, w, h);

      let settled = 0;
      for (const p of particles) {
        p.x += (p.tx - p.x) * 0.075;
        p.y += (p.ty - p.y) * 0.075;
        p.alpha = Math.min(1, p.alpha + 0.035);
        const d = Math.hypot(p.tx - p.x, p.ty - p.y);
        if (d < 2.5) settled += 1;

        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      if (started && settled === particles.length && particles.length > 0 && !finishScheduled) {
        finishScheduled = true;
        window.setTimeout(finish, 250);
      }

      raf = requestAnimationFrame(loop);
    };

    // Sample the logo into particles (prompt 13 — logo forming from gold dust)
    const off = document.createElement("canvas");
    off.width = 96;
    off.height = 96;
    const octx = off.getContext("2d", { willReadFrequently: true });
    const img = new window.Image();

    img.onload = () => {
      if (disposed) return;
      if (!octx) {
        finish();
        return;
      }
      octx.clearRect(0, 0, 96, 96);
      octx.drawImage(img, 0, 0, 96, 96);
      let data: Uint8ClampedArray;
      try {
        data = octx.getImageData(0, 0, 96, 96).data;
      } catch {
        finish();
        return;
      }

      const targets: { x: number; y: number }[] = [];
      for (let y = 0; y < 96; y += 2) {
        for (let x = 0; x < 96; x += 2) {
          const i = (y * 96 + x) * 4;
          if (data[i + 3] > 120) {
            targets.push({ x: CX + (x - 48) * scale, y: CY + (y - 48) * scale });
          }
        }
      }

      if (targets.length < 24) {
        finish();
        return;
      }

      const count = Math.min(targets.length, window.matchMedia("(max-width: 767px)").matches ? 600 : 1100);
      particles = new Array(count);
      for (let i = 0; i < count; i++) {
        const idx = (i * 977) % targets.length;
        const t = targets[idx];
        const ang = Math.random() * Math.PI * 2;
        const rad = Math.max(w, h) * (0.5 + Math.random() * 0.5);
        particles[i] = {
          x: CX + Math.cos(ang) * rad,
          y: CY + Math.sin(ang) * rad,
          tx: t.x,
          ty: t.y,
          size: 1.6 + Math.random() * 2.6,
          alpha: 0,
          color: `rgba(223, 192, 106, ${(0.7 + Math.random() * 0.3).toFixed(2)})`,
        };
      }

      started = true;
      raf = requestAnimationFrame(loop);
    };

    img.onerror = () => finish();
    img.src = "/images/logo-main.png";

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      clearTimeout(hardTimer);
      clearInterval(progressId);
    };
  }, [onComplete]);

  return (
    <div
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center transition-opacity duration-700 ${
        reveal ? "opacity-0" : "opacity-100"
      }`}
      style={{ background: "linear-gradient(180deg, #fbf8f2 0%, #f0eadd 100%)" }}
    >
      {/* Soft light vignette reveal */}
      <div
        className={`absolute inset-0 transition-all duration-[1.4s] ease-out ${
          reveal ? "scale-[1.6] opacity-0" : "scale-100 opacity-100"
        }`}
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 50% 45%, transparent 40%, rgba(240,232,214,0.95) 100%)",
        }}
      />

      {/* Light rays behind particle logo */}
      <div className="ray-layer left-[22%] top-[16%]" />
      <div className="ray-layer right-[16%] top-[30%]" style={{ animationDelay: "-6s" }} />

      {/* Particle logo canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />

      {/* Bottom progress */}
      <div className="relative flex flex-col items-center z-10" style={{ marginTop: "40vh" }}>
        <div className="w-56 h-px relative overflow-hidden" style={{ background: "rgba(201,168,76,0.25)" }}>
          <div
            className="absolute inset-y-0 left-0"
            style={{
              width: `${Math.min(progress, 100)}%`,
              background: "linear-gradient(90deg, rgba(201,168,76,0.4), #a8843a)",
            }}
          />
          <div
            className="absolute inset-y-0 w-4 blur-sm"
            style={{
              left: `calc(${Math.min(progress, 100)}% - 8px)`,
              background: "rgba(168,132,58,0.6)",
            }}
          />
        </div>
        <div className="mt-5 flex items-center gap-5">
          <span className="text-[11px] tracking-[0.15em] font-light tabular-nums" style={{ color: "rgba(138,106,44,0.7)" }}>
            {String(Math.floor(Math.min(progress, 100))).padStart(3, "0")}
          </span>
          <span className="text-[8px] tracking-[0.3em] uppercase" style={{ color: "rgba(138,106,44,0.6)" }}>
            Distilling the essence...
          </span>
        </div>
      </div>
    </div>
  );
}